import { useEffect, useState } from 'react';
import { Modal, Form, Radio, Space, Button, Tag, Typography, Empty, App } from 'antd';
import { useLanguages } from '@/hooks/useLanguages';
import { useStartTranslate } from '@/hooks/useTranslateBatch';
import { useTranslateJobsStore } from '@/stores/translateJobsStore';
import { AiTranslateProgress } from './AiTranslateProgress';

type OverwritePolicy = 'missing-only' | 'all';

interface Props {
  open: boolean;
  /** The topics to translate (topic ids from the list selection). */
  topicIds: string[];
  /** Called as each job finishes, so the caller can refresh the list. */
  onTranslated?: () => void;
  onClose: () => void;
}

/**
 * Bulk-translates the SELECTED topics into one or more target languages — one
 * translate-batch job per selected language, each scoped to the chosen topic
 * ids. English is the source and excluded; bundled zh-Hans is an eligible
 * target (topic content may be machine-translated into it).
 *
 * Handy when a new language is added: select every topic, pick the new
 * language, and translate them all in one action.
 */
export function TranslateSelectedModal({ open, topicIds, onTranslated, onClose }: Props) {
  const { message } = App.useApp();
  const langsQ = useLanguages();
  const startMut = useStartTranslate();
  const trackJob = useTranslateJobsStore((s) => s.track);

  const [overwrite, setOverwrite] = useState<OverwritePolicy>('missing-only');
  const [selected, setSelected] = useState<string[]>([]);
  const [started, setStarted] = useState<{ lang: string; jobId: string }[] | null>(null);

  // Every enabled language except the source (English).
  const targets = (langsQ.data ?? []).filter((l) => l.enabled && l.code !== 'en');
  const targetCodes = targets.map((l) => l.code);

  // Re-seed the selection each time the modal opens: default to every eligible
  // language (adding a new language → open → it's pre-checked).
  useEffect(() => {
    if (!open) return;
    setSelected(targetCodes);
    setStarted(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, langsQ.data]);

  const toggle = (code: string, on: boolean) =>
    setSelected((s) => (on ? [...new Set([...s, code])] : s.filter((c) => c !== code)));

  const selectAll = () => setSelected(targetCodes);
  const clear = () => setSelected([]);

  async function start() {
    if (selected.length === 0) { message.warning('Select at least one language'); return; }
    if (topicIds.length === 0) { message.warning('No topics selected'); return; }
    const chosen = targets.filter((l) => selected.includes(l.code));
    // allSettled (not all): if one language's job fails to start, we still show
    // progress for the ones that DID start — otherwise the form would stay up
    // and a re-click would re-POST the already-created jobs (duplicate work).
    const settled = await Promise.allSettled(
      chosen.map(async (l) => {
        const r = await startMut.mutateAsync({
          target_lang: l.code,
          source_lang: 'en',
          scope: 'topics',
          overwrite_policy: overwrite,
          topic_ids: topicIds,
        });
        trackJob(r.job_id);
        return { lang: l.code, jobId: r.job_id };
      }),
    );
    const ok = settled.flatMap((s) => (s.status === 'fulfilled' ? [s.value] : []));
    const failed = settled.flatMap((s, i) => (s.status === 'rejected' ? [chosen[i].code] : []));
    if (failed.length) message.error(`Failed to start: ${failed.join(', ')}`);
    if (ok.length) setStarted(ok);
  }

  function reset() {
    setStarted(null);
    onClose();
  }

  return (
    <Modal
      open={open}
      title={`Translate ${topicIds.length} topic${topicIds.length === 1 ? '' : 's'}`}
      onCancel={reset}
      footer={null}
      width={720}
      destroyOnHidden
    >
      {started ? (
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          {started.map((j) => (
            <div key={j.lang}>
              <Typography.Text strong>{j.lang}</Typography.Text>
              <AiTranslateProgress jobId={j.jobId} onTerminal={(s) => { if (s !== 'failed') onTranslated?.(); }} />
            </div>
          ))}
          <div style={{ textAlign: 'right' }}>
            <Button onClick={reset}>Close (jobs continue in background)</Button>
          </div>
        </Space>
      ) : (
        <Form layout="vertical">
          <Form.Item
            label={
              <Space>
                <span>Target languages</span>
                <Button size="small" type="link" onClick={selectAll}>All</Button>
                <Button size="small" type="link" onClick={clear}>Clear</Button>
              </Space>
            }
          >
            {targets.length > 0 ? (
              <Space wrap size={[8, 8]}>
                {targets.map((l) => (
                  <Tag.CheckableTag
                    key={l.code}
                    checked={selected.includes(l.code)}
                    onChange={(on) => toggle(l.code, on)}
                    style={{ border: '1px solid #d9d9d9', padding: '2px 10px', userSelect: 'none' }}
                  >
                    {l.code} — {l.name_native}
                  </Tag.CheckableTag>
                ))}
              </Space>
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No eligible target languages" />
            )}
          </Form.Item>
          <Form.Item label="Overwrite policy">
            <Radio.Group value={overwrite} onChange={(e) => setOverwrite(e.target.value)}>
              <Radio value="missing-only">Missing only (preserve existing translations)</Radio>
              <Radio value="all">Overwrite all</Radio>
            </Radio.Group>
          </Form.Item>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
            English is the source language and is excluded. One background job per
            language runs over all {topicIds.length} selected topic{topicIds.length === 1 ? '' : 's'}.
          </Typography.Paragraph>
          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button onClick={reset}>Cancel</Button>
              <Button type="primary" onClick={start} loading={startMut.isPending} disabled={selected.length === 0}>
                Translate {selected.length} language{selected.length === 1 ? '' : 's'}
              </Button>
            </Space>
          </div>
        </Form>
      )}
    </Modal>
  );
}
